import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Global } from '@emotion/react';
import isFunction from 'lodash/isFunction';
import { observer } from 'mobx-react';
import { Popover } from 'antd';
import { Skeleton } from 'components/common/skeleton';
import { UpOutlined, DownOutlined, SearchOutlined, PlusOutlined, InboxOutlined } from '@ant-design/icons';
import type { TreeDataNode, Tree, InputRef } from 'antd';
import useIsMobile from 'hooks/use-is-mobile';
import {
  Container,
  TriggerButton,
  TriggerContainer,
  IconContainer,
  TreeSelectorContainer,
  StyledTree,
  SearchContainer,
  StyledInput,
  FooterContainer,
  TreeNodeContainerBase,
  TreeNodeContainerLink,
  AddButton,
  TreeNodeTitle,
  FooterButton,
  SkeletonContainer,
  NoDataContainer,
  SectionLabel,
  TREE_VIEWPORT_HEIGHT_PX,
  TREE_ITEM_HEIGHT_PX,
  POPOVER_OVERLAY_CLASS,
  popoverOverlayStyles
} from 'components/common/popover-tree-selector/popover-tree-selector.utils';
import { useFilteredTreeData } from 'components/common/popover-tree-selector/use-filtered-tree-data';
import { usePopoverTreeScroll } from 'components/common/popover-tree-selector/use-popover-tree-scroll';
import { usePopoverTreeExpansion } from 'components/common/popover-tree-selector/use-popover-tree-expansion';
import { useTheme } from '@emotion/react';
import { intl } from 'components/localization';
import { getParentMap } from 'components/common/popover-tree-selector/tree-utils';

interface PopoverTreeSelectorProps {
  treeData: TreeDataNode[];
  selectedKeys: React.Key[];
  onSelect: (selectedKeys: React.Key[]) => void;
  showSearch?: boolean;
  searchPlaceholder: string;
  footerTitle?: string;
  onFooterClick?: () => void;
  headerContent?: React.ReactNode | ((close: () => void) => React.ReactNode);
  trigger?: React.ReactNode;
  onAddClick?: (key: React.Key) => void;
  isLoading?: boolean;
  dataE2e: string;
  getNodeUrl?: (key: React.Key) => string;
  popoverWidthPx?: number;
}

const PopoverTreeSelector = ({
  treeData,
  selectedKeys,
  onSelect,
  showSearch,
  searchPlaceholder,
  footerTitle,
  onFooterClick,
  headerContent,
  onAddClick,
  isLoading,
  trigger,
  dataE2e,
  getNodeUrl,
  popoverWidthPx
}: PopoverTreeSelectorProps) => {
  const { isMobile } = useIsMobile();
  const theme = useTheme();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const treeRef = useRef<React.ElementRef<typeof Tree>>(null);
  const searchInputRef = useRef<InputRef>(null);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
    setSearchValue('');
  }, [setIsPopoverOpen, setSearchValue]);

  const handleTreeSelect = useCallback(
    (keys: React.Key[]) => {
      if (keys.length === 0 && selectedKeys.length > 0) {
        return;
      }
      onSelect(keys);
      closePopover();
    },
    [selectedKeys, onSelect, closePopover]
  );

  const handleAddClick = useCallback(
    (nodeKey: React.Key, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onAddClick?.(nodeKey);
      closePopover();
    },
    [onAddClick, closePopover]
  );

  const footerClick = useCallback(() => {
    onFooterClick?.();
    closePopover();
  }, [onFooterClick, closePopover]);

  const renderTreeNodeTitle = useCallback(
    (nodeData: TreeDataNode, originalTitle: React.ReactNode) => {
      const nodeUrl = getNodeUrl?.(nodeData.key!);

      const content = (
        <>
          <TreeNodeTitle>{originalTitle}</TreeNodeTitle>
          {onAddClick && (nodeData as any).canCreate && (
            <AddButton onClick={e => handleAddClick(nodeData.key!, e)} data-e2e={`tree-node-add-${nodeData.key}`}>
              <PlusOutlined />
            </AddButton>
          )}
        </>
      );

      if (nodeUrl) {
        return (
          <TreeNodeContainerLink to={nodeUrl} data-e2e={`tree-node-title-${nodeData.key}`}>
            {content}
          </TreeNodeContainerLink>
        );
      }

      return <TreeNodeContainerBase data-e2e={`tree-node-title-${nodeData.key}`}>{content}</TreeNodeContainerBase>;
    },
    [onAddClick, handleAddClick, getNodeUrl]
  );

  const { filteredTreeData, searchMatchKeys } = useFilteredTreeData({
    treeData,
    searchValue,
    showSearch,
    renderTreeNodeTitle
  });

  const parentMap = useMemo(() => getParentMap(treeData), [treeData]);

  const { expandedKeys, handleExpand } = usePopoverTreeExpansion({
    isPopoverOpen,
    searchValue,
    selectedKey: selectedKeys?.[0],
    searchMatchKeys,
    parentMap
  });

  usePopoverTreeScroll({
    isPopoverOpen,
    scrollToKey: selectedKeys?.[0],
    parentMap,
    treeRef
  });

  useEffect(() => {
    if (isPopoverOpen && showSearch) {
      setTimeout(() => {
        searchInputRef.current?.focus({ cursor: 'end' });
      }, 0);
    }
  }, [isPopoverOpen, showSearch]);

  if (isLoading) {
    return (
      <SkeletonContainer data-e2e={`${dataE2e}-skeleton`}>
        <Skeleton.Input active />
      </SkeletonContainer>
    );
  }

  const popoverContent = (
    <TreeSelectorContainer $popoverWidthPx={popoverWidthPx}>
      {showSearch && (
        <SearchContainer>
          <StyledInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            ref={searchInputRef}
            data-e2e="search-input"
          />
        </SearchContainer>
      )}
      {isFunction(headerContent) ? headerContent(closePopover) : headerContent}
      <SectionLabel>All projects</SectionLabel>
      {filteredTreeData.length === 0 ? (
        <NoDataContainer>
          <InboxOutlined />
          <div>{intl.formatMessage({ id: 'popover-tree-selector.empty' })}</div>
        </NoDataContainer>
      ) : (
        <StyledTree
          ref={treeRef}
          treeData={filteredTreeData}
          onSelect={handleTreeSelect}
          selectedKeys={selectedKeys}
          blockNode
          expandedKeys={expandedKeys}
          onExpand={handleExpand}
          height={TREE_VIEWPORT_HEIGHT_PX}
          itemHeight={TREE_ITEM_HEIGHT_PX}
        />
      )}
      {footerTitle && (
        <FooterContainer>
          <FooterButton onClick={footerClick} data-e2e="footer-button">
            {footerTitle}
          </FooterButton>
        </FooterContainer>
      )}
    </TreeSelectorContainer>
  );

  return (
    <Container>
      <Global styles={popoverOverlayStyles(theme)} />
      <Popover
        content={popoverContent}
        trigger="click"
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement={isMobile ? 'bottom' : 'bottomLeft'}
        arrow={false}
        overlayClassName={POPOVER_OVERLAY_CLASS}>
        {/*
          Figma draws both selectors as Button instances -- the same Tertiary variant, padding and
          radius as the Docs button across the bar -- so the new design renders the real component
          rather than a second styled div shaped like one. Legacy keeps the div.

          Figma's top bar shows a single chevron; the stacked pair reads as a sort control. The icon
          container keeps `data-e2e` on both sides -- Cypress clicks it to open the popover.
        */}
        {theme.isNewDesign ? (
          <TriggerButton type="link">
            {trigger}
            <IconContainer data-e2e={dataE2e}>
              <DownOutlined />
            </IconContainer>
          </TriggerButton>
        ) : (
          <TriggerContainer>
            {trigger}
            <IconContainer data-e2e={dataE2e}>
              <UpOutlined />
              <DownOutlined />
            </IconContainer>
          </TriggerContainer>
        )}
      </Popover>
    </Container>
  );
};

export default observer(PopoverTreeSelector);
